import PaperBucketStrategy from './components/PaperBucketStrategy';
import Account from './components/Account';
import PageBoundary from './components/PageBoundary';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import SportsMarkets from './components/SportsMarkets';
import {GreyhoundMeetings,GreyhoundRace} from './components/Greyhounds';
import {InternationalRace} from './components/International';
import RaceDetail from './components/RaceDetail';
import HorseDetail from './components/HorseDetail';
import MyBets from './components/MyBets';
import NextToJump from './components/NextToJump';
import Results from './components/Results';
import DayResults from './components/DayResults';
import JockeyPerformance from './components/JockeyPerformance';
import Futures from './components/Futures';
import Login from './components/Login';
import Users from './components/Users';
import AdminNotes from './components/AdminNotes';
import BetfairConnect from './components/BetfairConnect';
import ClaudeStrategy from './components/ClaudeStrategy';
import ModelInspection from './components/ModelInspection';
import StrategyResults from './components/StrategyResults';
import SimpleBspControl from './components/SimpleBspControl';
import PrivatePaperBot from './components/PrivatePaperBot';
import ExternalIntelligence from './components/ExternalIntelligence';
import BucketBets from './components/BucketBets';
import FlowHistory from './components/FlowHistory';
import ProjectNavigation from './components/ProjectNavigation';
import { ToolsPage, ReportsPage, ArchivePage, NotFoundPage } from './components/ProjectPages';
import StakingBrainPaper from './components/StakingBrainPaper';
import Settings from './components/Settings';
import ReviewQueue from './components/ReviewQueue';
import BetSlipSidebar from './components/BetSlipSidebar';
import { BetSlipProvider } from './context/BetSlipContext';
import { AuthUser, getStoredUser, logout, verifySession, SESSION_EXPIRED_EVENT } from './api/auth';
import './App.css';
import './Brand.css';
import './Mobile.css';
import MobileNavigation from './components/MobileNavigation';

const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [authReady, setAuthReady] = useState(() => !!getStoredUser());
  // Responsive main navigation.
  const [navOpen, setNavOpen] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('');
  useEffect(() => {
    const expired = () => {
      setUser(null);
      setSessionMessage('Your session is no longer valid. Please sign in again to continue.');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
  }, []);

  // Validate the stored token on load; drop the session if it's stale.
  useEffect(() => {
    let cancelled = false;
    verifySession().then((u) => {
      if (!cancelled) { setUser(u); setAuthReady(true); }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoginSuccess = (loggedIn: AuthUser) => {
    setSessionMessage('');
    setUser(loggedIn);
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isPaperReviewer = user?.role === 'paper_reviewer';

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <header className="app-header">
          <div className="header-bar">
            <Link to="/" className="brand">
              <span className="brand-logo-frame"><img className="brand-logo" src="/brand/betbuilder-original.png" alt="Betbuilder — we dig the stats" width="2048" height="399" /></span>
            </Link>
            {isAuthenticated && <ProjectNavigation open={navOpen} close={() => setNavOpen(false)} admin={isAdmin} paperReviewer={isPaperReviewer} />}
            <div className="header-actions">
              {isAuthenticated && (
                <>
                  {!isPaperReviewer && <Link className="header-user" to="/account">Account settings</Link>}
                  <button className="logout-button" onClick={handleLogout}>
                    Log out
                  </button>
                  <button
                    type="button"
                    className={`nav-toggle${navOpen ? ' open' : ''}`}
                    aria-label={navOpen ? 'Close menu' : 'Open menu'}
                    aria-controls="main-navigation"
                    aria-expanded={navOpen}
                    onClick={() => setNavOpen((v) => !v)}
                  >
                    <span />
                    <span />
                    <span />
                  </button>
                </>
              )}
            </div>
          </div>
          {isAuthenticated && <MobileNavigation admin={isAdmin} paperReviewer={isPaperReviewer} close={() => setNavOpen(false)} />}
          {isAuthenticated && (
            <ProjectNavigation sectionOnly open={navOpen} close={() => setNavOpen(false)} admin={isAdmin} paperReviewer={isPaperReviewer} />
          )}
        </header>

        {isAuthenticated ? (
          <BetSlipProvider key={user!.id}>
            <main className="app-main">
              {isPaperReviewer ? <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/race/:meetingName/:raceNumber" element={<RaceDetail />} />
                <Route path="/horse/:horseName" element={<HorseDetail />} />
                <Route path="/sports" element={<Navigate to="/sports/afl" replace />} />
                <Route path="/sports/:sport" element={<SportsMarkets />} />
                <Route path="/sports/:sport/events/:eventId" element={<SportsMarkets />} />
                <Route path="/greyhounds" element={<GreyhoundMeetings />} />
                <Route path="/greyhounds/:eventId/races/:raceNumber" element={<GreyhoundRace />} /><Route path="/international/:eventId/races/:raceNumber" element={<InternationalRace />} />
                <Route path="/strategy/bucket-bets" element={<PaperBucketStrategy />} />
                <Route path="/strategy/buckets" element={<Navigate to="/strategy/bucket-bets" replace />} />
                <Route path="/strategy/auto" element={<SimpleBspControl paper />} />
                <Route path="/strategy/parlay" element={<SimpleBspControl paper />} />
                <Route path="/my-bets" element={<MyBets paper />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes> : <Routes>
                <Route path="/tools" element={<ToolsPage admin={isAdmin} />} />
                <Route path="/tools/archive" element={<ArchivePage />} />
                <Route path="/tools/flow-history" element={<FlowHistory />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/jockeys" element={<JockeyPerformance />} />
                <Route path="*" element={<NotFoundPage />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/sports" element={<Navigate to="/sports/afl" replace />} />
                <Route path="/greyhounds" element={<GreyhoundMeetings />} />
                <Route path="/greyhounds/:eventId/races/:raceNumber" element={<GreyhoundRace />} /><Route path="/international/:eventId/races/:raceNumber" element={<InternationalRace />} />
                <Route path="/sports/:sport" element={<SportsMarkets />} />
                <Route path="/sports/:sport/events/:eventId" element={<SportsMarkets />} />
                <Route path="/race/:meetingName/:raceNumber" element={<RaceDetail />} />
                <Route path="/horse/:horseName" element={<HorseDetail />} />
                <Route path="/next-to-jump" element={<NextToJump />} />
                <Route path="/results" element={<Results />} />
                <Route path="/results/day" element={<DayResults />} />
                <Route path="/futures" element={<Futures />} />
                <Route path="/my-bets" element={isAdmin ? <MyBets /> : <PrivatePaperBot key={user!.id} userId={user!.id} historyOnly />} />
                <Route path="/strategy" element={<PageBoundary><ClaudeStrategy /></PageBoundary>} />
                <Route path="/account" element={<Account />} /><Route path="/recover" element={<Account recovery />} />
                <Route path="/strategy/results" element={<StrategyResults />} />
                <Route path="/strategy/parlay" element={<Navigate to="/tools/archive" replace />} />
                <Route path="/strategy/place" element={<Navigate to="/tools/archive" replace />} />
                <Route path="/strategy/auto" element={isAdmin ? <SimpleBspControl /> : <PrivatePaperBot key={user!.id} userId={user!.id} />} />
                <Route path="/strategy/external-intelligence" element={<ExternalIntelligence />} />
                <Route path="/strategy/lanes" element={<Navigate to="/tools/archive" replace />} />
                <Route path="/strategy/buckets" element={<Navigate to="/strategy/bucket-bets" replace />} />
                <Route path="/strategy/bucket-bets" element={isAdmin ? <BucketBets /> : <PaperBucketStrategy />} />
                <Route path="/strategy/staking" element={<Navigate to="/tools/archive" replace />} />
                <Route path="/strategy/flow" element={<Navigate to="/tools/flow-history" replace />} />
                <Route path="/strategy/brain" element={<StakingBrainPaper />} />
                <Route path="/strategy/inspection" element={<ModelInspection />} />
                <Route path="/strategy/engines" element={<Navigate to="/tools/archive" replace />} />
                <Route path="/betfair" element={<BetfairConnect />} />
                {isAdmin && <Route path="/settings" element={<Settings />} />}
                {isAdmin && <Route path="/review" element={<ReviewQueue />} />}
                {isAdmin && <Route path="/all-notes" element={<AdminNotes />} />}
                {isAdmin && <Route path="/users" element={<Users />} />}
              </Routes>}
            </main>
            {(isAdmin || isPaperReviewer) && <BetSlipSidebar />}
          </BetSlipProvider>
        ) : !authReady ? (
          <main className="app-main" role="status">Restoring your session…</main>
        ) : (
          <>
            {sessionMessage && <p role="status" style={{textAlign:'center',padding:'12px 20px'}}>{sessionMessage}</p>}
            <Routes><Route path="/recover" element={<Account recovery />} /><Route path="*" element={<Login onSuccess={handleLoginSuccess} />} /></Routes>
          </>
        )}
      </div>
    </Router>
  );
};

export default App;
